import React, { Fragment, useCallback, useMemo } from 'react';
import { Disclosure, Tab } from '@headlessui/react';
import { MeetingStatus, Sorter } from './types';
import cx from 'classnames';
import EventLabel from '../../components/EventLabel/EventLabel';
import { KeyValue } from './types';
import { Menu, Transition } from '@headlessui/react';
import Avatar from './Avatar';
import TableHeader from './TableHeader';
import StatusBadge from './StatusBadge';
import { twMerge } from 'tailwind-merge';
import { useStore } from '../../store/Store';

interface IMeetingsProps {
  meetings: any[];
  myOrder: any;
  calendar: any[];
  orders: any[];
  settings: any;
  onSelect: (orderId: number) => void;
  onAcceptMeeting: (meetingId: number) => void;
  onCancelMeeting: (meeting: any) => void;
  onUnblockMeeting: (meeting: any) => void;
  onBlockMeeting: (timeslot: any) => void;
  onFixedTimeslot: (timeslot: any) => void;
}

const statusColor: KeyValue = {
  calendar: 'text-neutral-500',
  [MeetingStatus.BOOKED]: 'text-green-500',
  [MeetingStatus.PENDING]: 'text-sky-500',
  [MeetingStatus.CANCELLED]: 'text-red-500',
  [MeetingStatus.UNBOOKABLE]: 'text-amber-500',
  [MeetingStatus.FULL]: 'text-red-500',
};

const secondaryStatusColor: KeyValue = {
  calendar: 'bg-neutral-100',
  [MeetingStatus.BOOKED]: 'bg-green-100',
  [MeetingStatus.PENDING]: 'bg-sky-50 border border-sky-100',
  [MeetingStatus.CANCELLED]: 'bg-red-100',
  [MeetingStatus.UNBOOKABLE]: 'bg-amber-100',
};

const statusText: KeyValue = {
  [MeetingStatus.BOOKED]: 'CancelMeeting',
  [MeetingStatus.PENDING]: 'CancelMeeting',
  [MeetingStatus.CANCELLED]: 'BlockTimeslot',
  [MeetingStatus.UNBOOKABLE]: 'UnblockMeeting',
  default: 'BlockTimeslot',
};

const statusIcons: KeyValue = {
  calendar: 'ri-calendar-line',
  [MeetingStatus.BOOKED]: 'ri-check-line',
  [MeetingStatus.PENDING]: 'ri-time-line',
  [MeetingStatus.CANCELLED]: 'ri-close-line',
  [MeetingStatus.UNBOOKABLE]: 'ri-calendar-close-line',
  [MeetingStatus.FULL]: 'ri-calendar-close-line',
  [MeetingStatus.TIME_OUT]: 'ri-time-line',
  default: 'ri-calendar-close-line',
};

const actionIcons: KeyValue = {
  [MeetingStatus.BOOKED]: 'ri-close-line',
  [MeetingStatus.PENDING]: 'ri-close-line',
  [MeetingStatus.CANCELLED]: 'ri-calendar-close-line',
  [MeetingStatus.UNBOOKABLE]: 'ri-calendar-line',
  default: 'ri-calendar-close-line',
};

const actionColors: KeyValue = {
  [MeetingStatus.BOOKED]: 'text-red-500',
  [MeetingStatus.PENDING]: 'text-red-500',
  [MeetingStatus.CANCELLED]: 'text-amber-500',
  [MeetingStatus.UNBOOKABLE]: 'text-neutral-500',
  default: 'text-amber-500',
};

const statusColors: KeyValue = {
  [MeetingStatus.BOOKED]: 'text-green-600',
  [MeetingStatus.PENDING]: 'text-sky-600',
  [MeetingStatus.CANCELLED]: 'text-red-500',
  [MeetingStatus.UNBOOKABLE]: 'text-amber-500',
  [MeetingStatus.FULL]: 'text-red-500',
};

const labels: KeyValue = {
  [MeetingStatus.BOOKED]: 'MeetingBooked',
  [MeetingStatus.PENDING]: 'MeetingRequest',
  [MeetingStatus.CANCELLED]: 'MeetingCancelled',
};

export default function Meetings({
  meetings,
  orders,
  myOrder,
  settings,
  calendar,
  onSelect,
  onCancelMeeting,
  onAcceptMeeting,
  onBlockMeeting,
  onUnblockMeeting,
  onFixedTimeslot,
}: IMeetingsProps) {
  const [sortBy, setSortBy] = React.useState<string>('');
  const [desc, setDesc] = React.useState<boolean>(false);
  const [activeTab, setActiveTab] = React.useState<string>('calendar');

  const { questionAnswers = [] } = settings;

  const { environment } = useStore();
  const { lang } = environment;

  const data: any = useMemo(
    () => ({
      [MeetingStatus.CANCELLED]: meetings
        ?.filter(meeting => meeting.status?.toLowerCase() === MeetingStatus.CANCELLED?.toLowerCase())
        ?.map((meeting: any) => ({
          ...meeting,
          participant: meeting.participants?.find(p => p.order.orderId !== myOrder.orderId),
        })),
      [MeetingStatus.TIME_OUT]: meetings
        ?.filter(meeting => meeting.status?.toLowerCase() === MeetingStatus.TIME_OUT?.toLowerCase())
        ?.map((meeting: any) => ({
          ...meeting,
          participant: meeting.participants?.find(p => p.order.orderId !== myOrder.orderId),
        })),
    }),
    [meetings, activeTab, myOrder]
  );

  const timeslots = useMemo(() => {
    const timeSlotData = calendar
      .map(({ meetingTimeslotId, end, start }) => {
        const meetingMatch = meetings.find(
          m => m.status !== 'cancelled' && m.status !== 'timeout' && ((m.start == start && m.end == end) || (m.start < start && m.end > start) || (m.start < end && m.end >= end))
        );
        let counterPart = '--';

        const booker = meetingMatch?.participants?.find(p => p.role === 'booker');
        const target = meetingMatch?.participants?.find(p => p.role === 'target');

        const counterPartOrder = orders?.find(o => o.orderId === (booker?.orderId === myOrder.orderId ? target?.order?.orderId : booker?.order?.orderId));

        if (meetingMatch && meetingMatch?.status === MeetingStatus.UNBOOKABLE) {
          counterPart = MeetingStatus.UNBOOKABLE;
        } else if (meetingMatch && meetingMatch?.status === MeetingStatus.FULL) {
          counterPart = MeetingStatus.FULL;
        } else if (meetingMatch && meetingMatch?.status) {
          if (booker?.orderId === myOrder.orderId) {
            counterPart = target?.order?.person?.firstName + ' ' + target?.order?.person?.lastName;
          } else {
            counterPart = booker?.order?.person?.firstName + ' ' + booker?.order?.person?.lastName;
          }
        }

        return {
          meetingId: meetingMatch?.meetingId,
          meetingTimeslotId,
          end,
          start,
          counterPart,
          counterPartOrder,
          tableNumber: meetingMatch?.tableNumber,
          status: meetingMatch?.status ?? '',
          isRequested: meetingMatch?.participants?.find(p => p.orderId === myOrder.orderId)?.role !== 'booker',
          counterOrderId: meetingMatch?.participants?.find(p => p.orderId !== myOrder.orderId)?.orderId,
        };
      })
      ?.sort((a, b) => {
        if (sortBy === Sorter.TIME) {
          return desc ? +new Date(a.start) - +new Date(b.start) : +new Date(b.start) - +new Date(a.start);
        }
        if (sortBy === Sorter.NAME) {
          return desc ? a.counterPart.localeCompare(b.counterPart) : b.counterPart.localeCompare(a.counterPart);
        }
        if (sortBy === Sorter.STATUS) {
          return desc ? a?.status.localeCompare(b?.status) : b?.status.localeCompare(a?.status);
        }
      });

    return timeSlotData;
  }, [calendar, meetings, myOrder, sortBy, desc]);

  const timeslotAction = useCallback(
    ({ meetingId, meetingTimeslotId, status }: any) => {
      switch (status) {
        case MeetingStatus.BOOKED:
        case MeetingStatus.PENDING: {
          return onCancelMeeting(meetingId);
        }
        case MeetingStatus.UNBOOKABLE: {
          return onUnblockMeeting(meetingId);
        }
        default: {
          return onBlockMeeting(meetingTimeslotId);
        }
      }
    },
    [onBlockMeeting, onCancelMeeting, onUnblockMeeting]
  );

  const days = useMemo(() => {
    return timeslots
      ?.map(({ start }) => new Date(start).toLocaleDateString())
      ?.filter((v, i, a) => a.indexOf(v) === i)
      ?.sort((a, b) => {
        if (sortBy === Sorter.TIME) {
          return desc ? b.localeCompare(a) : a.localeCompare(b);
        }
        return a.localeCompare(b);
      });
  }, [calendar, desc, sortBy, timeslots]);

  const handleSortBy = useCallback(
    (newSorter: Sorter) => {
      if (sortBy === newSorter) {
        setDesc(prev => !prev);
      } else {
        setDesc(false);
      }
      setSortBy(newSorter);
    },
    [sortBy, desc]
  );

  return (
    <Tab.Group onChange={index => setActiveTab(index === 0 ? 'calendar' : index === 1 ? MeetingStatus.CANCELLED : MeetingStatus.TIME_OUT)}>
      <Tab.List className="bg-[whitesmoke] w-full flex max-w-full [&>button]:w-1/2 md:[&>button]:w-fit overflow-x-auto">
        {['calendar', MeetingStatus.CANCELLED, MeetingStatus.TIME_OUT]
          .filter(s => s !== MeetingStatus.UNBOOKABLE)
          .map(tab => (
            <Tab
              className={({ selected }) =>
                cx('focus:outline-0 py-3', selected ? 'bg-white border-b-none  md:w-fit border-b-transparent ' : '', 'border-r border-r-divider')
              }
              key={tab}
            >
              <div className="flex gap-x-2 items-center justify-center px-2">
                <i className={cx(statusIcons?.[tab as string] ?? '', statusColor?.[tab as string])} />
                <div className="flex items-center gap-x-2 text-sm md:text-md">
                  <EventLabel name={labels[tab as string] ?? tab} groupName="CMS.MatchMaking" />
                </div>
              </div>
            </Tab>
          ))}
      </Tab.List>
      <div>
        {((data?.[activeTab]?.length > 0 && activeTab !== 'calendar') || activeTab === 'calendar') && (
          <TableHeader onSort={handleSortBy} desc={desc} sortBy={sortBy} />
        )}
        <div className="divide-y divide-divider lg:max-h-128 overflow-y-auto overflow-x-hidden relative">
          {data?.[activeTab]?.length === 0 && activeTab !== 'calendar' && (
            <div className="p-3 py-6 flex items-center text-sm md:text-md">
              <EventLabel name="NoMeetings" groupName="CMS.MatchMaking" />
            </div>
          )}
          {activeTab === 'calendar' && (
            <>
              {days.map(day => (
                <Disclosure key={day} defaultOpen as="div" className="disclosure bg-[whitesmoke]">
                  {({ open }) => (
                    <>
                      <Disclosure.Button
                        className={twMerge(
                          'flex w-full justify-between px-4  bg-white py-4 pl-7 text-left text-sm border-t-0  relative focus:outline-none font-medium items-center '
                        )}
                      >
                        <span className="flex items-center gap-x-2 text-sm md:text-md">
                          <i className="ri-calendar-line" />
                          <span className="font-bold text-md">{day}</span>
                        </span>
                        <i className={cx('ri-arrow-down-s-line mx-2', open ? 'rotate-180 transform' : '')} />
                      </Disclosure.Button>
                      <Transition
                        show={!!open}
                        enter="transition duration-100 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition duration-75 ease-out"
                        leaveFrom="transform scale-100 opacity-100"
                        leaveTo="transform scale-95 opacity-0"
                      >
                        <Disclosure.Panel className="panel text-gray-500 p-2  pb-4 text-sm  relative border-t border-divider ">
                          {timeslots
                            ?.filter(t => new Date(t.start).toLocaleDateString() === day)
                            ?.map(
                              ({
                                meetingTimeslotId,
                                end,
                                meetingId,
                                start,
                                counterPart,
                                status,
                                isRequested,
                                tableNumber,
                                counterOrderId,
                                counterPartOrder,
                                ...rest
                              }) => (
                                <div
                                  key={meetingTimeslotId}
                                  className={cx(
                                    'grid relative grid-cols-12 w-full gap-x-2 -gap-y-1 bg-white grid-rows-2 lg:grid-rows-1 mt-2 border  rounded-md  border-divider'
                                  )}
                                >
                                  <div className="font-normal row-span-full col-span-3 block lg:hidden lg:col-span-2 py-3 lg:h-14  px-4 ">
                                    <div className="flex lg:justify-start items-center h-full">
                                      {counterOrderId ? (
                                        <Avatar person={orders?.find(o => o.orderId === counterOrderId)} className="w-8 h-8 lg:w-10 lg:h-10" />
                                      ) : status === MeetingStatus.UNBOOKABLE ? (
                                        <div className="flex-center bg-amber-50 w-8 h-8 lg:w-10 lg:h-10 rounded-full border border-amber-100">
                                          <i className={cx(statusIcons?.[status], statusColors?.[status])} />
                                        </div>
                                      ) : status === MeetingStatus.FULL ? (
                                        <div className="flex-center bg-red-50 w-8 h-8 lg:w-10 lg:h-10 rounded-full border border-red-100">
                                          <i className={cx(statusIcons?.[status], statusColors?.[status])} />
                                        </div>
                                      ) : (
                                        <div className="w-8 md:w-12 flex-center">
                                          <button
                                            onClick={() =>
                                              onFixedTimeslot({
                                                meetingTimeslotId,
                                                end,
                                                meetingId,
                                                start,
                                                counterPart,
                                                status,
                                                isRequested,
                                                counterOrderId,
                                                ...rest,
                                              })
                                            }
                                            className="rounded-full cursor-pointer w-8 h-8 lg:w-10 lg:h-10 bg-neutral-100 border-dashed border-2  border-divider flex-center"
                                          >
                                            <i className="ri-add-line " />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div className="col-span-7 lg:col-span-3 lg:px-4 row-span-1 items-end pt-3 text-sm md:text-md lg:py-3  lg:h-14 flex lg:items-center">
                                    <div className="flex flex-col ">
                                      <span>
                                        {`${new Date(start).toLocaleTimeString('sv-SE', {
                                          hour: '2-digit',
                                          minute: '2-digit',
                                        })} - ${new Date(end).toLocaleTimeString('sv-SE', {
                                          hour: '2-digit',
                                          minute: '2-digit',
                                        })}`}
                                      </span>
                                      {tableNumber && (
                                        <span className="font-semibold">
                                          <EventLabel name="TableNo" groupName="CMS.MatchMaking" /> {tableNumber}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div
                                    className={cx(
                                      'lg:col-span-6 row-span-1 lg:row-span-1 text-sm md:text-md flex items-center  lg:h-14  pb-3 lg:pb-0',
                                      status === MeetingStatus.UNBOOKABLE ? 'col-span-8' : 'col-span-7'
                                    )}
                                  >
                                    <div className="mr-4 font-normal hidden lg:flex py-4">
                                      {counterOrderId ? (
                                        <Avatar person={orders?.find(o => o.orderId === counterOrderId)} className="w-8 h-8 lg:w-10 lg:h-10" />
                                      ) : status === MeetingStatus.UNBOOKABLE ? (
                                        <div className="w-10 flex-center bg-amber-50 h-10 rounded-full border border-amber-100">
                                          <i className={cx(statusIcons?.[status], statusColors?.[status])} />
                                        </div>
                                      ) : status === MeetingStatus.FULL ? (
                                        <div className="flex-center bg-red-50 w-8 h-8 lg:w-10 lg:h-10 rounded-full border border-red-100">
                                          <i className={cx(statusIcons?.[MeetingStatus.FULL], statusColors?.[MeetingStatus.FULL])} />
                                        </div>
                                      ) : (
                                        <div className="w-10 flex-center">
                                          <button
                                            onClick={() =>
                                              onFixedTimeslot({
                                                meetingTimeslotId,
                                                end,
                                                meetingId,
                                                start,
                                                counterPart,
                                                status,
                                                isRequested,
                                                counterOrderId,
                                                ...rest,
                                              })
                                            }
                                            className="rounded-full cursor-pointer w-10 h-10 bg-neutral-100 border-dashed border-2 border-divider flex-center"
                                          >
                                            <i className="ri-add-line " />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                    <span
                                      onMouseDown={() => counterOrderId && status === MeetingStatus.BOOKED && onSelect(counterOrderId)}
                                      className="flex items-center"
                                    >
                                      <div className="flex flex-col py-2">
                                        <span
                                          className={cx(
                                            counterOrderId && status === MeetingStatus.BOOKED ? 'underline cursor-pointer ' : '',
                                            'font-semibold'
                                          )}
                                        >
                                          {counterPart === 'unbookable' ? (
                                            <EventLabel name="TimeslotTableUnbookable" groupName="CMS.MatchMaking" />
                                          ) : counterPart === 'full' ? (
                                            <EventLabel name="TimeslotTableFull" groupName="CMS.MatchMaking" />
                                          ) : (
                                            counterPart
                                          )}
                                          {counterPartOrder && counterPartOrder?.companyName && ` • ${counterPartOrder?.companyName}`}
                                        </span>
                                        <span className="text-xs ">
                                          {questionAnswers?.length > 0 &&
                                            counterPartOrder &&
                                            counterPartOrder?.questions
                                              ?.filter(q => questionAnswers.includes(q.questionId))
                                              ?.map((q: any) => q.questionOptionLabel?.find((o: any) => o.languageCode === lang)?.label)
                                              ?.join(' • ')}
                                        </span>
                                      </div>

                                      {counterPart !== '--' && counterPart !== 'unbookable' && counterPart !== 'full' && status === MeetingStatus.BOOKED && (
                                        <i className="ri-arrow-right-line no-underline ml-1" />
                                      )}
                              
                                    </span>
                                  </div>
                                  {status === MeetingStatus.PENDING || status === MeetingStatus.BOOKED ? (
                                    <div className="lg:col-span-3 flex col-start-11 col-end-12 row-span-full lg:row-span-1 items-center">
                                      <StatusBadge status={status} isRequested={isRequested} />
                                    </div>
                                  ) : null}
                                  <div className="w-full bg-white rounded-bl-md rounded-br-md col-span-12 px-2 flex justify-start pt-1 pb-3">
                                    {status === MeetingStatus.PENDING || status === MeetingStatus.BOOKED ? (
                                      <div className="flex gap-x-1">
                                        {isRequested && status === MeetingStatus.PENDING ? (
                                          <button
                                            className="bg-white border-divider border gap-x-1 justify-center  px-2 py-1 rounded-md flex text-sm"
                                            onClick={() => onAcceptMeeting(meetingId)}
                                          >
                                            <span>
                                              <i className={cx('ri-check-line', statusColor?.[MeetingStatus.BOOKED])} />
                                            </span>
                                            <span className="">
                                              <EventLabel name="AcceptMeeting" groupName="CMS.MatchMaking" />
                                            </span>
                                          </button>
                                        ) : null}
                                        <button
                                          className="bg-white border-divider border gap-x-1 px-2 py-1 justify-center  rounded-md flex text-sm"
                                          onClick={() => onCancelMeeting(meetingId)}
                                        >
                                          <span>
                                            <i className={cx('ri-close-line ', actionColors?.[status])} />
                                          </span>
                                          <span className="">
                                            <EventLabel name="CancelMeeting" groupName="CMS.MatchMaking" />
                                          </span>
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => timeslotAction({ meetingTimeslotId, status, meetingId })}
                                        className="bg-white  border-divider border gap-x-2 px-2 py-1 rounded-md justify-center flex text-sm"
                                      >
                                        <i
                                          className={cx(
                                            actionIcons?.[status as string] ?? actionIcons?.default,
                                            actionColors?.[status as string] ?? actionColors?.default
                                          )}
                                        />
                                        <EventLabel name={statusText?.[status as string] ?? statusText?.default} groupName="CMS.MatchMaking" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )
                            )}
                        </Disclosure.Panel>
                      </Transition>
                    </>
                  )}
                </Disclosure>
              ))}
            </>
          )}

          {activeTab !== 'calendar' &&
            data?.[activeTab]?.map(({ start, end, status, participant, isRequested }: any) => (
              <div className={cx('grid grid-cols-12 w-full gap-x-2 -gap-y-1 grid-rows-2 lg:grid-rows-1    overflow-hidden border-divider')}>
                <div className="font-normal row-span-full col-span-3 block lg:hidden lg:col-span-2 py-3 px-4 ">
                  <div className="flex lg:justify-start items-center h-full">
                    {participant?.order?.orderId ? (
                      <Avatar className="w-8 h-8 lg:w-10 lg:h-10" person={orders?.find(o => o.orderId === participant?.order?.orderId)} />
                    ) : null}
                  </div>
                </div>
                <div className="col-span-7 lg:col-span-3 lg:px-4 row-span-1 items-end pt-3 text-sm md:text-md lg:py-3 flex lg:items-center">
                  {`${new Date(start).toLocaleTimeString('sv-SE', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })} - ${new Date(end).toLocaleTimeString('sv-SE', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}`}
                </div>
                <div className="lg:col-span-6 col-span-7  row-span-1 text-sm md:text-md flex items-center pb-3 lg:pb-0">
                  <div className="mr-4 font-normal hidden lg:flex py-4">
                    {participant?.order?.orderId ? (
                      <Avatar className="w-8 h-8 lg:w-10 lg:h-10" person={orders?.find(o => o.orderId === participant?.order?.orderId)} />
                    ) : null}
                  </div>
                  <div>
                    <span className="font-semibold">
                      {participant?.order?.person?.firstName} {participant?.order?.person?.lastName}
                    </span>
                    {participant?.order?.person?.company?.name}
                  </div>
                </div>
                <div className="lg:col-span-3 flex col-start-11 col-end-12 row-span-full lg:row-span-1 items-center">
                  <StatusBadge status={status} isRequested={isRequested} />
                </div>
                {/* <div className="w-full  font-normal col-span-12 px-2 gap-x-2 bg-[whitesmoke] flex lg:hidden text-sm md:text-md justify-start py-3 border-t border-divider">
                  <span className='bg-amber-100 p-1 rounded-full text-amber-700'>
                    {participants?.find(p => p.status === 'declined')?.order?.person?.firstName} <EventLabel name="Declined" groupName="CMS.MatchMaking" />
                  </span>
                </div> */}
              </div>
            ))}
        </div>
      </div>
    </Tab.Group>
  );
}
